import { computed, inject, Injectable, Signal } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

const screens = {
  'sm'  : '640px',
  'md'  : '768px',
  'lg'  : '1024px',
  'xl'  : '1280px',
  '2xl' : '1536px',
};

type TailwindBreakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type TailwindBreakpointState<T extends TailwindBreakpoint[]> = {
  [K in T[number]]: boolean;
};

@Injectable({ providedIn: 'root' })
export class TailwindService {

  private breakpointObserver = inject(BreakpointObserver);

  private breakpointMap = Object.entries(screens).reduce(
    (acc, [breakpoint, width]) => ({ ...acc, [breakpoint]: this.createMinWidth(width) }),
    {} as Record<TailwindBreakpoint, Signal<boolean>>
  );

  observe<T extends TailwindBreakpoint[]>(...breakpoints: T): Signal<TailwindBreakpointState<T>> {
    return computed(() => breakpoints.reduce(
        (acc, breakpoint) => ({ ...acc, [breakpoint]: this.breakpointMap[breakpoint]() }),
        {} as TailwindBreakpointState<T>
      )
    );
  }

  private createMinWidth(minWidth: string): Signal<boolean> {
    const query = `(min-width: ${minWidth})`;

    return toSignal(
      this.breakpointObserver.observe([query]).pipe(
        map(result => result.matches),
      ),
      { initialValue: this.breakpointObserver.isMatched(query) }
    );
  }
}
